import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import {
  LocalizationProvider,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DesktopDateRangePicker } from "@mui/x-date-pickers-pro/DesktopDateRangePicker";
import {
  ArrowDropDownOutlined as ArrowDropDownOutlinedIcon,
  ArrowDropUpOutlined as ArrowDropUpOutlinedIcon
} from "@mui/icons-material";
import dayjs, { weekdaysMin } from "dayjs";
import weekday from "dayjs/plugin/weekday";
import updateLocale from "dayjs/plugin/updateLocale";
import localeData from "dayjs/plugin/localeData";
import "dayjs/locale/en-ca";
import "dayjs/locale/en";
import "./CustomDatePicker.css";
import CustomYearPicker from "./CustomYearPicker";
import CustomMonthPicker from "./CustomMonthPicker";
import { Box, IconButton, Typography, Popper } from "@mui/material";
import _isEqual from "lodash/isEqual";
import InputAdornment from "@mui/material/InputAdornment";
import { CalendarMonthIcon } from "@/ImageRepo";
import axios from "@/features/axiosHelper";

const CustomDatePicker = ({
  dates,
  setDates,
  onApply,
  customZIndex = 1300,
  heapIOParentPage = null, //Label for heap.io use
  preferenceKey = null, // e.g., "HomepageDateFilter" or "IntegrationsDateFilter"
}) => {
  const [selectedMonth, setSelectedMonth] = useState(-1);
  const [selectedYear, setSelectedYear] = useState("");
  const [monthPicker, setMonthPicker] = useState(false);
  const [yearPicker, setYearPicker] = useState(false);
  const [posTop, setPosTop] = useState(0);
  const [posLeft, setPosLeft] = useState(0);
  const [datePicker, setDatePicker] = useState(false);
  const [rangePosition, setRangePosition] = useState("start");
  const [openedStartISO, setOpenedStartISO] = useState(null);

  const onDateChange = (selectedDates) => {
    //force drop any time component
    const startDate = selectedDates[0] && dayjs(selectedDates[0]).isValid() 
    ? dayjs(dayjs(selectedDates[0]).format("YYYY-MM-DD")) : null;
    const endDate = selectedDates[1] && dayjs(selectedDates[1]).isValid() 
    ? dayjs(dayjs(selectedDates[1]).format("YYYY-MM-DD")) : null;
    
    setDates([startDate, endDate]);

    // heap.io: Only track date filter changes if page label is set (for event naming)
    if (heapIOParentPage && startDate && endDate) {
      const eventName = `${heapIOParentPage} - Update Date Filter`;

      const startDateText = `${startDate.$y}-${startDate.$M + 1}-${
        startDate.$D
      }`;
      const endDateText = `${endDate.$y}-${endDate.$M + 1}-${endDate.$D}`;

      const eventProps = {
        "Filter - Start": startDateText,
        "Filter - End": endDateText,
      };

      window.heap.track(eventName, eventProps);
    }
  };

  const checkSingleDateSelected = () => {
    //check if only one date is selected, then make both dates the same
    if (dates[1] === null) {
      setDates([dates[0], dates[0]]);
    }
    setDatePicker(false);
  };

  const createMonthAndYearIcon = () => {
    const div = document.createElement("div");
    const root = createRoot(div);
    flushSync(() => {
      root.render(<ArrowDropDownOutlinedIcon className="arrowMonthYear" />);
    });
    return div.innerHTML;
  };

  const attachMonthAndYearIcon = () => {
    setTimeout(() => {
      const yearSelector = document.getElementsByClassName(
        "MuiPickersCalendarHeader-label"
      )[0];
      yearSelector.insertAdjacentHTML("beforeend", createMonthAndYearIcon());
    }, 1);
  };

  const toggleMonthSwitcherArrows = (toggle) => {
    const monthArrowSwitcher = document.getElementsByClassName(
      "MuiPickersArrowSwitcher-root"
    )[0];
    if (toggle) {
      monthArrowSwitcher.style.display = "none";
    } else {
      monthArrowSwitcher.style.display = "flex";
    }
  };

  const getSelectedMonth = (event) => {
    setSelectedMonth(event.target.id);
  };

  const getSelectedYear = (event) => {
    setSelectedYear(event.target.value);
  };

  const showDatePicker = (toggle) => {
    setDatePicker(toggle);
    if (document.getElementsByClassName("arrowMonthYear")[0] === undefined)
      attachMonthAndYearIcon();
  };

  const getDatePickerPosition = () => {
    return [
      document
        .getElementsByClassName("MuiDayCalendar-header")[0]
        .getBoundingClientRect().top,
      document
        .getElementsByClassName("MuiPickersPopper-root")[0]
        .getBoundingClientRect().left,
    ];
  };

  const getYearPickerPosition = () => {
    return [
      document
        .getElementsByClassName("yearContainer")[0]
        .getBoundingClientRect().top,
      document
        .getElementsByClassName("yearContainer")[0]
        .getBoundingClientRect().left,
    ];
  };

  const getMonthPickerPosition = () => {
    return [
      document
        .getElementsByClassName("monthContainer")[0]
        .getBoundingClientRect().top,
      document
        .getElementsByClassName("monthContainer")[0]
        .getBoundingClientRect().left,
    ];
  };

  const setYearPickerPosition = () => {
    const datePicker = getDatePickerPosition();
    const yearPicker = getYearPickerPosition();

    if (!_isEqual(datePicker, yearPicker)) {
      setPosTop(datePicker[0] - yearPicker[0]);
      setPosLeft(datePicker[1] - yearPicker[1]);
    }
  };

  const setMonthPickerPosition = () => {
    const datePicker = getDatePickerPosition();
    const monthPicker = getMonthPickerPosition();

    if (!_isEqual(datePicker, monthPicker)) {
      setPosTop(datePicker[0] - monthPicker[0]);
      setPosLeft(datePicker[1] - monthPicker[1]);
    }
  };

  const showMonthPicker = () => {
    setYearPicker(false);
    setMonthPicker(true);
    if (!monthPicker) {
      setTimeout(() => {
        document.getElementsByClassName("monthContainer")[0].style.display =
          "flex";
        setMonthPickerPosition();
      });
    }
  };

  const showYearPicker = () => {
    setYearPicker((yearPicker) => !yearPicker);
    if (!yearPicker) {
      setSelectedYear("");
      toggleMonthSwitcherArrows(true);
      setTimeout(() => {
        document.getElementsByClassName("yearContainer")[0].style.display =
          "flex";
        setYearPickerPosition();
      });
    } else {
      toggleMonthSwitcherArrows(false);
    }
  };

  // for future use
  const highlightSelectedDay = (toggle) => {
    setTimeout(() => {
      const selectedDay = document.getElementsByClassName(
        "MuiButtonBase-root MuiPickersDay-root Mui-selected MuiDateRangePickerDay-day"
      )[0];

      if (selectedDay !== undefined) {
        if (toggle) {
          console.log("added");
          selectedDay.classList.add("Mui-selected");
        } else {
          console.log("removed");
          selectedDay.classList.remove("Mui-selected");
        }
      }
    }, 5);
  };

  const savePreferredStartDate = async (date) => {
    if (!date || !preferenceKey) return;
    if (document.visibilityState !== "visible") return;

    try {
      const payload = {
        startDateISO: dayjs(date).toISOString(),
        expiresAtISO: dayjs().add(7, "day").toISOString(),
      };
      await axios.post("/account/user-preferences", null, {
        params: {
          key: preferenceKey,
          value: JSON.stringify(payload),
        },
      });
    } catch (error) {
      console.error("Error saving date preference:", error);
    }
  };

  useEffect(() => {
    if (selectedYear !== "") {
      if (rangePosition === "start") {
        setDates([
          dayjs().set("date", 1).set("month", 0).set("year", selectedYear),
          dayjs(""),
        ]);
      } else if (rangePosition === "end") {
        setDates([
          dates[0],
          dayjs().set("date", 1).set("month", 0).set("year", selectedYear),
        ]);
      }
    }
  }, [selectedYear]);

  useEffect(() => {
    if (selectedMonth !== -1) {
      if (rangePosition === "start") {
        setDates([
          dayjs()
            .set("date", 1)
            .set("month", selectedMonth)
            .set("year", selectedYear),
          dayjs(""),
        ]);
      } else if (rangePosition === "end") {
        setDates([
          dates[0],
          dayjs()
            .set("date", 1)
            .set("month", selectedMonth)
            .set("year", selectedYear),
        ]);
      }
      toggleMonthSwitcherArrows(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    const handleClick = (event) => {
      const clickedElement = event.target;
      if (
        clickedElement.classList.contains("arrowMonthYear") ||
        clickedElement.classList.contains("MuiPickersCalendarHeader-label")
      ) {
        setMonthPicker(false);
        showYearPicker();
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [yearPicker]);

  const handleOnClose = () => {
    if (yearPicker && selectedYear !== "") {
      showMonthPicker();
    } else if (yearPicker && selectedYear === "") {
      setYearPicker(false);
      setSelectedYear("");
      toggleMonthSwitcherArrows(false);
    } else if (monthPicker) {
      setMonthPicker(false);
      setSelectedMonth(-1);
      toggleMonthSwitcherArrows(false);
    } else {
      setDatePicker(false);
    }

    // Persist only when a complete and changed range exists
    const start = dates?.[0];
    const end = dates?.[1];
    const startValid = !!start && dayjs(start).isValid();
    const endValid = !!end && dayjs(end).isValid();
    if (openedStartISO && startValid && endValid) {
      const currentStartISO = dayjs(start).startOf("day").toISOString();
      if (currentStartISO !== openedStartISO) {
        savePreferredStartDate(start);
      }
    }
    setOpenedStartISO(null);
  };

  const handleIconClick = () => {
    const opening = !datePicker;
    showDatePicker(opening); // Toggle date picker visibility
    if (opening) {
      // Snapshot the start date when opening
      const start = dates?.[0];
      setOpenedStartISO(
        !!start && dayjs(start).isValid()
          ? dayjs(start).startOf("day").toISOString()
          : null
      );
    } else {
      setOpenedStartISO(null);
    }
  };

  return (
    <Box position="relative">
      <Box
        display="flex"
        alignItems="center"
        onClick={handleIconClick}
        sx={{ cursor: "pointer" }}
        className="date-filter-container"
      >
        <IconButton size="small">
          <CalendarMonthIcon className="custom-calendar-icon" />
        </IconButton>
        <Typography
          sx={{
            fontFamily: '"Heebo", sans-serif',
            height: "20px",
            color: "var(--neutral-5)",
          }}
        >
          Period:
          {dates[0] && dayjs(dates[0]).isValid()
            ? dayjs(dates[0]).format(" YYYY.MM.DD")
            : "YYYY.MM.DD"}{" "}
          -{" "}
          {dates[1] && dayjs(dates[1]).isValid()
            ? dayjs(dates[1]).format("YYYY.MM.DD")
            : "YYYY.MM.DD"}
        </Typography>
      </Box>

      {datePicker && (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="en"
          localeText={{
            cancelButtonLabel: "Cancel",
            okButtonLabel: "Apply",
          }}
        >
          <Typography className="date-label">Date</Typography>
          <DesktopDateRangePicker
            calendars={1}
            slots={{
              leftArrowIcon: ArrowDropUpOutlinedIcon,
              field: SingleInputDateRangeField,
              rightArrowIcon: ArrowDropDownOutlinedIcon,
            }}
            slotProps={{
              textField: {
                error: false,
                className: "custom-text-field",
                onClick: () => showDatePicker(true),
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <CalendarMonthIcon className="datepicker-calendar-icon" />
                    </InputAdornment>
                  ),
                },
              },
              popper: {
                style: { zIndex: customZIndex },
                modifiers: [{ name: "flip", enabled: false }],
                PopperProps: {
                  // style: { zIndex: 6, position: "absolute", top: "60px" }, // adjust position as needed
                  className: "custom-popper-style",
                },
              },
            }}
            closeOnSelect={false}
            label="Date Picker"
            showDaysOutsideCurrentMonth
            value={dates}
            onChange={(selectedDates) => onDateChange(selectedDates)}
            onMonthChange={() => attachMonthAndYearIcon()}
            onClose={() => handleOnClose()}
            open={datePicker}
            onAccept={() => {
              checkSingleDateSelected();
              onApply();
            }}
            onRangePositionChange={(position) => setRangePosition(position)}
          />
          {monthPicker && (
            <CustomMonthPicker
              getSelectedMonth={getSelectedMonth}
              positionTop={posTop}
              positionLeft={posLeft}
            />
          )}
          {yearPicker && (
            <CustomYearPicker
              getSelectedYear={getSelectedYear}
              positionTop={posTop}
              positionLeft={posLeft}
            />
          )}
        </LocalizationProvider>
      )}
    </Box>
  );
};

export default CustomDatePicker;
